package com.mgacreative.hotelru.feature.finance.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.domain.repository.CurrentAccount
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CurrentAccountsScreen(
    viewModel: FinanceViewModel = koinViewModel(),
    onNavigateBack: () -> Unit = {},
    onNavigate: (String) -> Unit = {}
) {
    val accessError by viewModel.accessError.collectAsState()
    val accounts by viewModel.accounts.collectAsState()

    var showAddDialog by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedTypeFilter by remember { mutableStateOf("ALL") } // ALL, SUPPLIER, CUSTOMER, AGENCY

    val totalReceivable = accounts.filter { it.balance > 0 }.sumOf { it.balance }
    val totalPayable = accounts.filter { it.balance < 0 }.sumOf { kotlin.math.abs(it.balance) }
    val netBalance = totalReceivable - totalPayable

    val filteredAccounts = accounts.filter { acc ->
        val matchesType = when (selectedTypeFilter) {
            "SUPPLIER" -> acc.type.equals("SUPPLIER", ignoreCase = true) || acc.type.equals("Tedarikçi", ignoreCase = true)
            "CUSTOMER" -> acc.type.equals("CUSTOMER", ignoreCase = true) || acc.type.equals("Müşteri", ignoreCase = true)
            "AGENCY" -> acc.type.equals("AGENCY", ignoreCase = true) || acc.type.equals("Acente", ignoreCase = true)
            else -> true
        }
        val matchesSearch = searchQuery.isBlank() || acc.name.contains(searchQuery, ignoreCase = true)
        matchesType && matchesSearch
    }

    if (showAddDialog) {
        AddCurrentAccountDialog(
            onDismiss = { showAddDialog = false },
            onAdd = { name, type, balance ->
                viewModel.addCurrentAccount(name, type, balance)
                showAddDialog = false
            }
        )
    }

    val listState = rememberLazyListState()

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(
                            onClick = onNavigateBack,
                            modifier = Modifier
                                .size(36.dp)
                                .background(Color(0xFFF1F5F9), CircleShape)
                        ) {
                            Icon(
                                Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = l10n("Back"),
                                tint = Color(0xFF1E293B),
                                modifier = Modifier.size(18.dp)
                            )
                        }
                        Spacer(modifier = Modifier.width(12.dp))
                        Column {
                            Text(
                                text = l10n("Cari Hesaplar & Faturalar"),
                                fontWeight = FontWeight.Bold,
                                fontSize = 18.sp,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = l10n("Müşteri, Tedarikçi ve Acente Bakiyeleri"),
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Button(
                        onClick = { showAddDialog = true },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                        shape = RoundedCornerShape(10.dp),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp)
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(
                            text = l10n("Yeni Cari Hesap"),
                            fontSize = 13.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = Color.White
                        )
                    }
                }
            }
        },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        bottomBar = {
            FinanceBottomNavBar(
                selectedTab = 1,
                onNavigate = onNavigate
            )
        }
    ) { paddingValues ->
        if (accessError != null) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                Surface(
                    shape = RoundedCornerShape(16.dp),
                    color = Color.White,
                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFFEE2E2)),
                    modifier = Modifier.padding(24.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(Icons.Default.Warning, contentDescription = null, tint = Color(0xFFDC2626), modifier = Modifier.size(48.dp))
                        Spacer(modifier = Modifier.height(12.dp))
                        Text(accessError ?: "", color = Color(0xFFDC2626), fontWeight = FontWeight.Bold, fontSize = 15.sp)
                    }
                }
            }
        } else {
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
                    .background(Color(0xFFF8FAFC))
                    .verticalScrollbar(listState),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // KPI Cards
                item {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        // Toplam Alacak
                        Surface(
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White,
                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            shadowElevation = 2.dp
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(l10n("Toplam Alacak"), color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                                    Box(
                                        modifier = Modifier.size(24.dp).background(Color(0xFFDCFCE7), CircleShape),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Icon(Icons.Default.KeyboardArrowUp, contentDescription = null, tint = Color(0xFF16A34A), modifier = Modifier.size(16.dp))
                                    }
                                }
                                Spacer(modifier = Modifier.height(6.dp))
                                Text("₽ ${totalReceivable.format()}", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF16A34A))
                            }
                        }

                        // Toplam Borç
                        Surface(
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White,
                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            shadowElevation = 2.dp
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(l10n("Toplam Borç"), color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                                    Box(
                                        modifier = Modifier.size(24.dp).background(Color(0xFFFEE2E2), CircleShape),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Icon(Icons.Default.KeyboardArrowDown, contentDescription = null, tint = Color(0xFFDC2626), modifier = Modifier.size(16.dp))
                                    }
                                }
                                Spacer(modifier = Modifier.height(6.dp))
                                Text("₽ ${totalPayable.format()}", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFFDC2626))
                            }
                        }

                        // Net Cari Durum
                        Surface(
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White,
                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            shadowElevation = 2.dp
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(l10n("Net Durum"), color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                                    Box(
                                        modifier = Modifier.clip(RoundedCornerShape(6.dp)).background(Color(0xFFEFF6FF)).padding(horizontal = 6.dp, vertical = 2.dp)
                                    ) {
                                        Text(l10n("Cari Net"), color = Color(0xFF2563EB), fontSize = 10.sp, fontWeight = FontWeight.Bold)
                                    }
                                }
                                Spacer(modifier = Modifier.height(6.dp))
                                Text(
                                    text = "₽ ${netBalance.format()}",
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 18.sp,
                                    color = if (netBalance >= 0) Color(0xFF2563EB) else Color(0xFFDC2626)
                                )
                            }
                        }
                    }
                }

                // Filter and Search Row
                item {
                    Surface(
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(12.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            OutlinedTextField(
                                value = searchQuery,
                                onValueChange = { searchQuery = it },
                                placeholder = { Text(l10n("Hesap / Firma ara..."), fontSize = 13.sp, color = Color(0xFF94A3B8)) },
                                leadingIcon = {
                                    Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(18.dp))
                                },
                                trailingIcon = {
                                    if (searchQuery.isNotEmpty()) {
                                        IconButton(onClick = { searchQuery = "" }) {
                                            Icon(Icons.Default.Clear, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(16.dp))
                                        }
                                    }
                                },
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp),
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = Color(0xFF2563EB),
                                    unfocusedBorderColor = Color(0xFFE2E8F0),
                                    focusedContainerColor = Color.White,
                                    unfocusedContainerColor = Color(0xFFF8FAFC)
                                ),
                                modifier = Modifier
                                    .weight(1f)
                                    .height(48.dp)
                            )

                            Spacer(modifier = Modifier.width(12.dp))

                            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                FinanceFilterChip(
                                    label = l10n("Tümü"),
                                    count = accounts.size,
                                    isSelected = selectedTypeFilter == "ALL",
                                    onClick = { selectedTypeFilter = "ALL" }
                                )
                                FinanceFilterChip(
                                    label = l10n("Tedarikçi"),
                                    count = accounts.count { it.type.equals("SUPPLIER", true) || it.type.equals("Tedarikçi", true) },
                                    isSelected = selectedTypeFilter == "SUPPLIER",
                                    selectedColor = Color(0xFFD97706),
                                    onClick = { selectedTypeFilter = "SUPPLIER" }
                                )
                                FinanceFilterChip(
                                    label = l10n("Müşteri"),
                                    count = accounts.count { it.type.equals("CUSTOMER", true) || it.type.equals("Müşteri", true) },
                                    isSelected = selectedTypeFilter == "CUSTOMER",
                                    selectedColor = Color(0xFF2563EB),
                                    onClick = { selectedTypeFilter = "CUSTOMER" }
                                )
                                FinanceFilterChip(
                                    label = l10n("Acente"),
                                    count = accounts.count { it.type.equals("AGENCY", true) || it.type.equals("Acente", true) },
                                    isSelected = selectedTypeFilter == "AGENCY",
                                    selectedColor = Color(0xFF7C3AED),
                                    onClick = { selectedTypeFilter = "AGENCY" }
                                )
                            }
                        }
                    }
                }

                // Account List
                if (filteredAccounts.isEmpty()) {
                    item {
                        Surface(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 24.dp),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White,
                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
                        ) {
                            Column(
                                modifier = Modifier.padding(36.dp),
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(64.dp)
                                        .background(Color(0xFFEFF6FF), CircleShape),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Icon(
                                        Icons.Default.AccountBox,
                                        contentDescription = null,
                                        tint = Color(0xFF2563EB),
                                        modifier = Modifier.size(32.dp)
                                    )
                                }
                                Spacer(modifier = Modifier.height(16.dp))
                                Text(
                                    text = l10n("Henüz Cari Hesap Kaydı Yok"),
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 16.sp,
                                    color = Color(0xFF0F172A)
                                )
                                Spacer(modifier = Modifier.height(6.dp))
                                Text(
                                    text = l10n("Tedarikçi, acente veya müşterilerinizin cari bakiyelerini takip etmek için ilk cari hesabı oluşturun."),
                                    fontSize = 13.sp,
                                    color = Color(0xFF64748B),
                                    textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                                    modifier = Modifier.widthIn(max = 400.dp)
                                )
                                Spacer(modifier = Modifier.height(20.dp))
                                Button(
                                    onClick = { showAddDialog = true },
                                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                                    shape = RoundedCornerShape(10.dp),
                                    contentPadding = PaddingValues(horizontal = 20.dp, vertical = 10.dp)
                                ) {
                                    Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(l10n("İlk Cari Hesabı Ekle"), fontSize = 13.sp, fontWeight = FontWeight.Bold)
                                }
                            }
                        }
                    }
                } else {
                    items(filteredAccounts) { acc ->
                        ModernCurrentAccountCard(acc)
                    }
                }

                item {
                    Spacer(modifier = Modifier.height(40.dp))
                }
            }
        }
    }
}

@Composable
fun ModernCurrentAccountCard(acc: CurrentAccount) {
    val isSupplier = acc.type.equals("SUPPLIER", true) || acc.type.equals("Tedarikçi", true)
    val isCustomer = acc.type.equals("CUSTOMER", true) || acc.type.equals("Müşteri", true)
    val isAgency = acc.type.equals("AGENCY", true) || acc.type.equals("Acente", true)

    val typeColor = when {
        isSupplier -> Color(0xFFD97706)
        isAgency -> Color(0xFF7C3AED)
        else -> Color(0xFF2563EB)
    }

    val typeBg = when {
        isSupplier -> Color(0xFFFEF3C7)
        isAgency -> Color(0xFFEDE9FE)
        else -> Color(0xFFEFF6FF)
    }

    val typeLabel = when {
        isSupplier -> l10n("Tedarikçi")
        isAgency -> l10n("Acente")
        isCustomer -> l10n("Müşteri")
        else -> acc.type
    }

    val balanceColor = if (acc.balance >= 0) Color(0xFF16A34A) else Color(0xFFDC2626)
    val balanceBg = if (acc.balance >= 0) Color(0xFFDCFCE7) else Color(0xFFFEE2E2)

    Surface(
        shape = RoundedCornerShape(14.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        shadowElevation = 1.dp,
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .size(44.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .background(typeBg),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = when {
                            isSupplier -> Icons.Default.ShoppingCart
                            isAgency -> Icons.Default.Star
                            else -> Icons.Default.AccountBox
                        },
                        contentDescription = null,
                        tint = typeColor,
                        modifier = Modifier.size(24.dp)
                    )
                }

                Spacer(modifier = Modifier.width(14.dp))

                Column {
                    Text(
                        text = acc.name,
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp,
                        color = Color(0xFF0F172A)
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(6.dp))
                            .background(typeBg)
                            .padding(horizontal = 8.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = typeLabel,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = typeColor
                        )
                    }
                }
            }

            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = if (acc.balance >= 0) l10n("Alacak Bakiyesi") else l10n("Borç Bakiyesi"),
                    fontSize = 11.sp,
                    color = Color(0xFF64748B),
                    fontWeight = FontWeight.Medium
                )
                Spacer(modifier = Modifier.height(2.dp))
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .background(balanceBg)
                        .padding(horizontal = 10.dp, vertical = 4.dp)
                ) {
                    Text(
                        text = "₽ ${acc.balance.format()}",
                        color = balanceColor,
                        fontWeight = FontWeight.ExtraBold,
                        fontSize = 15.sp
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddCurrentAccountDialog(
    onDismiss: () -> Unit,
    onAdd: (name: String, type: String, balance: Double) -> Unit
) {
    var name by remember { mutableStateOf("") }
    var balance by remember { mutableStateOf("0.00") }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    val accountTypes = listOf("Tedarikçi", "Müşteri", "Acente", "Diğer")
    val typeCodes = mapOf("Tedarikçi" to "SUPPLIER", "Müşteri" to "CUSTOMER", "Acente" to "AGENCY", "Diğer" to "OTHER")
    var expanded by remember { mutableStateOf(false) }
    var selectedType by remember { mutableStateOf(accountTypes.first()) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = l10n("Yeni Cari Hesap Ekle"),
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = Color(0xFF0F172A)
                )
                IconButton(onClick = onDismiss, modifier = Modifier.size(28.dp)) {
                    Icon(Icons.Default.Close, contentDescription = l10n("Kapat"), tint = Color(0xFF64748B))
                }
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                // Name
                OutlinedTextField(
                    value = name,
                    onValueChange = {
                        name = it
                        errorMessage = null
                    },
                    label = { Text(l10n("Hesap / Firma Adı"), fontSize = 12.sp) },
                    placeholder = { Text("Örn: Metro Grossmarket Ltd.", color = Color(0xFF94A3B8)) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1),
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color(0xFFF8FAFC)
                    ),
                    modifier = Modifier.fillMaxWidth()
                )

                // Type Dropdown
                ExposedDropdownMenuBox(
                    expanded = expanded,
                    onExpandedChange = { expanded = !expanded }
                ) {
                    OutlinedTextField(
                        value = l10n(selectedType),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(l10n("Hesap Türü"), fontSize = 12.sp) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        ),
                        modifier = Modifier
                            .menuAnchor()
                            .fillMaxWidth()
                    )
                    ExposedDropdownMenu(
                        expanded = expanded,
                        onDismissRequest = { expanded = false },
                        modifier = Modifier.background(Color.White)
                    ) {
                        accountTypes.forEach { type ->
                            DropdownMenuItem(
                                text = { Text(l10n(type), fontSize = 13.sp, color = Color(0xFF0F172A)) },
                                onClick = {
                                    selectedType = type
                                    expanded = false
                                }
                            )
                        }
                    }
                }

                // Initial Balance
                OutlinedTextField(
                    value = balance,
                    onValueChange = { balance = it },
                    label = { Text(l10n("Açılış Bakiyesi") + " (₽)", fontSize = 12.sp) },
                    placeholder = { Text("0.00", color = Color(0xFF94A3B8)) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1),
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color(0xFFF8FAFC)
                    ),
                    modifier = Modifier.fillMaxWidth()
                )

                if (errorMessage != null) {
                    Text(
                        text = errorMessage ?: "",
                        color = Color(0xFFDC2626),
                        fontSize = 12.sp
                    )
                }
            }
        },
        confirmButton = {
            val emptyNameError = l10n("Lütfen cari hesap adı giriniz.")
            Button(
                onClick = {
                    if (name.isBlank()) {
                        errorMessage = emptyNameError
                        return@Button
                    }
                    val parsedBalance = balance.replace(",", ".").toDoubleOrNull() ?: 0.0
                    val typeCode = typeCodes[selectedType] ?: "OTHER"
                    onAdd(name.trim(), typeCode, parsedBalance)
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(10.dp),
                contentPadding = PaddingValues(horizontal = 20.dp, vertical = 10.dp)
            ) {
                Text(l10n("Kaydet"), fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(
                onClick = onDismiss,
                shape = RoundedCornerShape(10.dp)
            ) {
                Text(l10n("İptal"), color = Color(0xFF64748B))
            }
        }
    )
}
